import { RECURRENCE_EDIT_SCOPES, RECURRENCE_EDIT_SCOPE_LABELS, type RecurrenceEditScope } from '@hearth/shared';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { Button, Dialog } from '../../components/ui';

type ScopeAction = 'edit' | 'delete';
type AskOptions = {
  /** False when the change cannot apply to one occurrence alone (a new repeat rule): "This event" is not offered. */
  allowThis?: boolean;
};
type Pending = { action: ScopeAction; scopes: readonly RecurrenceEditScope[]; resolve: (scope: RecurrenceEditScope | null) => void };

/**
 * Asks which occurrences of a repeating entry an edit or delete applies to — Google Calendar's "This event /
 * This and following events / All events". Render `element` once; `ask` resolves null on cancel, Esc or backdrop.
 *
 * ```tsx
 * const scopePrompt = useRecurrenceScopePrompt();
 * const scope = task.seriesId ? await scopePrompt.ask('edit') : 'this';
 * if (!scope) return;
 * …
 * {scopePrompt.element}
 * ```
 */
export function useRecurrenceScopePrompt(): {
  ask: (action: ScopeAction, options?: AskOptions) => Promise<RecurrenceEditScope | null>;
  element: ReactNode;
} {
  const [pending, setPending] = useState<Pending | null>(null);
  const [scope, setScope] = useState<RecurrenceEditScope>('this');
  const pendingRef = useRef<Pending | null>(null);

  const ask = useCallback(
    (action: ScopeAction, { allowThis = true }: AskOptions = {}) =>
      new Promise<RecurrenceEditScope | null>((resolve) => {
        // A new question supersedes an unanswered one (treated as cancelled).
        pendingRef.current?.resolve(null);
        const scopes = allowThis ? RECURRENCE_EDIT_SCOPES : RECURRENCE_EDIT_SCOPES.filter((value) => value !== 'this');
        const next = { action, scopes, resolve };
        pendingRef.current = next;
        setScope(scopes[0] ?? 'following');
        setPending(next);
      }),
    [],
  );

  const settle = (value: RecurrenceEditScope | null) => {
    pendingRef.current?.resolve(value);
    pendingRef.current = null;
    setPending(null);
  };

  const deleting = pending?.action === 'delete';
  const element = (
    <Dialog
      open={pending !== null}
      onClose={() => settle(null)}
      title={deleting ? 'Delete repeating entry' : 'Change repeating entry'}
      description="Completed occurrences are kept as they are."
      size="sm"
      footer={
        <>
          <Button onClick={() => settle(null)}>Cancel</Button>
          <Button variant={deleting ? 'danger' : 'primary'} onClick={() => settle(scope)} data-autofocus>
            {deleting ? 'Delete' : 'Save'}
          </Button>
        </>
      }
    >
      <fieldset className="space-y-2">
        <legend className="sr-only">Apply to</legend>
        {(pending?.scopes ?? RECURRENCE_EDIT_SCOPES).map((value) => (
          <label key={value} className="flex cursor-pointer items-center gap-2 text-[13px] text-slate-800">
            <input
              type="radio"
              name="recurrence-scope"
              value={value}
              checked={scope === value}
              onChange={() => setScope(value)}
              className="size-4 accent-ember-600"
            />
            {RECURRENCE_EDIT_SCOPE_LABELS[value]}
          </label>
        ))}
      </fieldset>
    </Dialog>
  );

  return { ask, element };
}
